/**
 * Owns Contract Review's internal identity and optional source grouping.
 *
 * Manual uploads and Vault connectors share one document model: DocReview
 * assigns every file an internal request ID, while connectors may add stable
 * external identity. Grouping is an explicit presentation choice so existing
 * flat workspaces never change merely because provenance becomes available.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, FolderSync, Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { workspaceConfigKeys, useWorkspaceConfig } from '@/app-shell/hooks/useWorkspaceConfig'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
} from '@/components/ui'
import { getWorkspacesApi } from '@/subsystems/workspaces'

interface WorkspaceSettings {
  requestPrefix: string | null
  groupDocumentsByExternalId: boolean
}

export function WorkspaceGeneralSettings({
  orgId,
  workspaceId,
  connectorEligible,
}: {
  orgId: string
  workspaceId: string
  connectorEligible: boolean
}) {
  const queryClient = useQueryClient()
  const { enablePrefixSetting } = useWorkspaceConfig(orgId, workspaceId)
  const prefixRequired = Boolean(enablePrefixSetting || connectorEligible)
  const [prefix, setPrefix] = useState('')
  const [groupByExternalId, setGroupByExternalId] = useState(false)
  const [saved, setSaved] = useState({ prefix: '', groupByExternalId: false })
  const [error, setError] = useState<string | null>(null)
  const settings = useQuery<WorkspaceSettings>({
    queryKey: ['workspace-settings', orgId, workspaceId],
    queryFn: async () => (
      await getWorkspacesApi().get<WorkspaceSettings>(
        `/workspaces/${orgId}/${workspaceId}/settings`,
      )
    ).data,
  })
  const dirty = prefix !== saved.prefix || groupByExternalId !== saved.groupByExternalId

  useEffect(() => {
    if (!settings.data) return
    const next = {
      prefix: settings.data.requestPrefix ?? '',
      groupByExternalId: settings.data.groupDocumentsByExternalId,
    }
    setPrefix(next.prefix)
    setGroupByExternalId(next.groupByExternalId)
    setSaved(next)
  }, [settings.data])

  const save = useMutation({
    mutationFn: () => getWorkspacesApi().patch(
      `/workspaces/${orgId}/${workspaceId}/settings`,
      {
        requestPrefix: prefix,
        groupDocumentsByExternalId: groupByExternalId,
      },
    ),
    onSuccess: async () => {
      setSaved({ prefix, groupByExternalId })
      setError(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['workspace-settings', orgId, workspaceId] }),
        queryClient.invalidateQueries({ queryKey: workspaceConfigKeys.config(orgId, workspaceId) }),
      ])
      toast.success('Workspace settings saved')
    },
    onError: (cause: unknown) => toast.error(
      (cause as { response?: { data?: { detail?: string } } }).response?.data?.detail
      ?? 'Failed to save settings',
    ),
  })

  if (!prefixRequired) {
    return (
      <Card>
        <CardHeader><CardTitle>General</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          This application has no additional general settings.
        </CardContent>
      </Card>
    )
  }

  const saveSettings = () => {
    if (prefixRequired && !prefix) {
      setError('Request ID prefix is required')
      return
    }
    save.mutate()
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Document intake</CardTitle>
        <CardDescription>
          Manual uploads and Vault connectors use the same DocReview document identity.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 md:grid-cols-2">
          <IntakePath
              icon={Upload}
              title="Manual file uploads"
              description="People add documents directly; DocReview assigns the internal request ID."
          />
          {connectorEligible && (
            <IntakePath
              icon={FolderSync}
              title="External systems via Vault"
              description="Connectors add source identity while DocReview still assigns the internal request ID."
            />
          )}
        </div>

        {prefixRequired && (
          <div className="max-w-xl space-y-2 border-t pt-5">
            <Label htmlFor="request-prefix">Request ID prefix</Label>
            <Input
              id="request-prefix"
              value={prefix}
              maxLength={10}
              disabled={save.isPending}
              className={error ? 'border-destructive' : undefined}
              onChange={(event) => {
                setPrefix(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                setError(null)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Uppercase letters and numbers only. New requests are numbered automatically.
            </p>
          </div>
        )}

        {connectorEligible && (
          <div className="flex items-center justify-between gap-5 rounded-xl border p-4">
            <div className="space-y-1">
              <Label htmlFor="group-by-external-id" className="text-sm font-semibold">
                Group connector documents by source record
              </Label>
              <p className="max-w-2xl text-xs leading-5 text-muted-foreground">
                Show documents from the same award, subaward, negotiation, or other external
                record under one expandable row. Leave off to keep this workspace flat.
              </p>
            </div>
            <Switch
              id="group-by-external-id"
              checked={groupByExternalId}
              disabled={save.isPending}
              onCheckedChange={setGroupByExternalId}
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button disabled={!dirty || save.isPending} onClick={saveSettings}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
          {!dirty && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Check className="h-3.5 w-3.5" /> Settings saved
            </span>
          )}
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </CardContent>
    </Card>
  )
}

function IntakePath({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Upload
  title: string
  description: string
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border bg-slate-50/60 p-4">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-600" />
      <span>
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-1 block text-xs font-normal leading-5 text-slate-500">
          {description}
        </span>
      </span>
    </div>
  )
}
